import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { Button, Checkbox, Icon, Paper, Typography } from "@mui/material";
import { Alert } from "@mui/material";

import LocationBase from "./Location";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import LocationAdvancedSettingBase from "./LocationAdvancedSetting";
import useSearchFilter from "@/helpers/hooks/useSearchFilter";
import RpVirtualListBase from "@/components/virtual/RpVirtualList";
import MuiSearchInputBase from "@/components/muiCustom/search/MuiSearchInput";
import JoinCampaignBulkSelectionBase from "./bulkSelection/JoinCampaignBulkSelection";
import RpSvgIconBase from "@/components/icons/RpSvgIcon";
import useIsParticipant from "@/features/auth/hooks/useIsParticipant";
import StateCityLocationFilter, {
  doesLocationMatchStateCityFilter,
  EMPTY_STATE_CITY_FILTER,
  StateCityAppliedFilterChips,
} from "@/features/campaigns/components/joinCampaignCommon/StateCityLocationFilter";
import BulkBudgetRecommendationProgress from "@/features/campaigns/components/budgetRecommendation/BulkBudgetRecommendationProgress";
import useBulkBudgetRecommendation from "./hooks/useBulkBudgetRecommendation";

type LocationId = string | number;

type LocationData = {
  id: LocationId;
  groups?: Array<{ id: LocationId; name: string }> | null;
  location_name?: string;
  state?: string;
  country?: string;
  city?: string;
  creatives?: unknown[];
  system_generated_location_id?: LocationId;
  budget?: {
    brand_match_preset_amount?: string | number | null;
  };
  [key: string]: unknown;
};

type LocationFormData = {
  budget?: string | number | null;
  [key: string]: any;
};

type BulkRecommendationOptions = Parameters<
  typeof useBulkBudgetRecommendation
>[0];

type SubscribedLocationsProps = {
  onGetBudgetRecommendation?: React.ComponentProps<
    typeof LocationBase
  >["onGetBudgetRecommendation"];
  onGetBulkBudgetRecommendation?: BulkRecommendationOptions["onGetBulkBudgetRecommendation"];
  onStreamBulkBudgetRecommendationProgress?: BulkRecommendationOptions["onStreamBulkBudgetRecommendationProgress"];
  isBudgetRecommendationEnabled?: boolean;
  onBudgetRecommendationInProgressChange?: (_inProgress: boolean) => void;
  campaign: Record<string, any>;
  values: Record<LocationId, LocationFormData>;
  subscribedLocations?: LocationData[];
  isSubmitting?: boolean;
  setFieldValue: (_field: LocationId, _value: LocationFormData) => void;
  setFieldError?: (..._args: unknown[]) => void;
  onSaveAsDraft?: React.MouseEventHandler<HTMLButtonElement>;
  productList?: unknown;
  getAdvanceSettingData?: unknown;
  fillAdvancedSetting: boolean;
  setFillAdvancedSetting: (_value: boolean) => void;
  isAdvancedSettingsFormValid?: boolean;
  handleOnChangeAdvancedSettingError?: (..._args: unknown[]) => void;
  expanded: LocationId[];
  setExpanded: (_expanded: LocationId[]) => void;
};

const Location = LocationBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const LocationAdvancedSetting =
  LocationAdvancedSettingBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const RpVirtualList = RpVirtualListBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const MuiSearchInput = MuiSearchInputBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const JoinCampaignBulkSelection =
  JoinCampaignBulkSelectionBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const RpSvgIcon = RpSvgIconBase as unknown as React.ElementType;
export default function SubscribedLocations({
  campaign,
  values,
  subscribedLocations = [],
  isSubmitting,
  setFieldValue,
  setFieldError,
  onSaveAsDraft,
  productList,
  getAdvanceSettingData,
  fillAdvancedSetting,
  setFillAdvancedSetting,
  isAdvancedSettingsFormValid,
  handleOnChangeAdvancedSettingError,
  expanded,
  setExpanded,
  onGetBudgetRecommendation,
  onGetBulkBudgetRecommendation,
  onStreamBulkBudgetRecommendationProgress,
  isBudgetRecommendationEnabled = false,
  onBudgetRecommendationInProgressChange,
}: SubscribedLocationsProps) {
  const isPreview = !useIsParticipant();
  const [checkedLocations, setCheckedLocations] = useState<LocationId[]>([]);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const frozenSortedLocationsRef = useRef([]);
  const [appliedLocationFilters, setAppliedLocationFilters] = useState(
    EMPTY_STATE_CITY_FILTER
  );
  // Sort function that we'll reuse
  const getSortedLocations = useCallback(
    (locations: LocationData[]) => {
      const { locIds } = joinCampaignHelper.getValidLocations(values, campaign);
      const filledLocations = locations.filter((l) =>
        locIds.includes(String(l.id))
      );
      const notFilledLocations = locations.filter(
        (l) => !locIds.includes(String(l.id))
      );
      return [...filledLocations, ...notFilledLocations];
    },
    [values, campaign]
  );

  const currentSortedSubscribedLocations = useMemo(() => {
    return getSortedLocations(subscribedLocations);
  }, [subscribedLocations, getSortedLocations]);

  if (!isInteracting) {
    frozenSortedLocationsRef.current = currentSortedSubscribedLocations;
  }

  const sortedSubscribedLocations = useMemo(() => {
    // When interacting, return the previously sorted list
    if (isInteracting) {
      return frozenSortedLocationsRef.current;
    }
    // When not interacting, return current sorted list
    return currentSortedSubscribedLocations;
  }, [currentSortedSubscribedLocations, isInteracting]);

  const handleInteractionStart = useCallback(() => {
    setIsInteracting((prev) => (prev ? prev : true));
  }, []);

  const handleInteractionEnd = useCallback(() => {
    setIsInteracting((prev) => (prev ? false : prev));
  }, []);

  const unCheckedLocations = useCallback(
    (locId: LocationId) => {
      // remove checked locations that are expanded
      if (expanded.includes(locId)) {
        setCheckedLocations((p) => p.filter((id) => id !== locId));
      }
    },
    [setCheckedLocations, expanded]
  );

  const { campaign_advanced_setting } = campaign;

  const {
    progress: bulkRecommendationProgress,
    visibleLocations: visibleBulkRecommendationLocations,
    visibleCompleted: visibleBulkRecommendationCompleted,
    removedLocationCount: removedBulkRecommendationLocationCount,
    isInProgress: isBulkRecommendationInProgress,
    handleRequested: handleBulkBudgetRecommendationRequested,
    handleCancel: handleCancelBulkRecommendation,
    handleClose: handleCloseBulkRecommendationProgress,
    handleBudgetChange: handleBulkRecommendationBudgetChange,
    handleRetry: handleRetryBulkRecommendation,
    handleToggleMinimized: handleToggleBulkRecommendationMinimized,
  } = useBulkBudgetRecommendation({
    campaign,
    values,
    subscribedLocations,
    setFieldValue,
    onGetBulkBudgetRecommendation,
    onStreamBulkBudgetRecommendationProgress,
  });

  const filledLocations = useMemo(() => {
    const { locIds } = joinCampaignHelper.getValidLocations(values, campaign);

    return subscribedLocations.filter((l) => locIds.includes(String(l.id)));
  }, [values, campaign, subscribedLocations]);

  const locationFilteredSubscribedLocations = useMemo(() => {
    return sortedSubscribedLocations.filter((loc) =>
      doesLocationMatchStateCityFilter(loc, appliedLocationFilters)
    );
  }, [appliedLocationFilters, sortedSubscribedLocations]);

  const [searchKey, handleSearchChange, filteredSubscribedLocations] =
    useSearchFilter(locationFilteredSubscribedLocations, ["location_name"]);
  const visibleLocationIds = useMemo(
    () => new Set<LocationId>(filteredSubscribedLocations.map((loc) => loc.id)),
    [filteredSubscribedLocations]
  );
  const visibleCheckedLocations = checkedLocations.filter((id) =>
    visibleLocationIds.has(id)
  );
  const isAllLocationsChecked =
    visibleLocationIds.size > 0 &&
    visibleCheckedLocations.length === visibleLocationIds.size;

  const handleToggleAllLocations = useCallback(
    (checked) => {
      setCheckedLocations((previous) =>
        checked
          ? Array.from(new Set([...previous, ...visibleLocationIds]))
          : previous.filter((id) => !visibleLocationIds.has(id))
      );
    },
    [visibleLocationIds]
  );

  const getTotal = useMemo(() => {
    // getting total of only fully filled locations
    return joinCampaignHelper
      .getTotal(joinCampaignHelper.getValidLocations(values, campaign)?.locs)
      ?.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  }, [values, campaign]);

  const madeSelectionsIn = useMemo(() => {
    return joinCampaignHelper.getValidLocations(values, campaign)?.locIds
      ?.length;
  }, [values, campaign]);

  const isSingleRecommendationInProgress = Object.values(values || {}).some(
    (locFormData) => Boolean(locFormData?.isBudgetRecommendationLoading)
  );
  const isBudgetRecommendationInProgress =
    isBulkRecommendationInProgress || isSingleRecommendationInProgress;

  useEffect(() => {
    onBudgetRecommendationInProgressChange?.(isBudgetRecommendationInProgress);
  }, [
    isBudgetRecommendationInProgress,
    onBudgetRecommendationInProgressChange,
  ]);

  if (!subscribedLocations || subscribedLocations.length === 0) {
    return (
      <div className="my-5 text-center">
        <Alert severity="info">
          <strong>There is no active location.</strong>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      {subscribedLocations?.length > 1 && fillAdvancedSetting === false ? (
        <JoinCampaignBulkSelection
          creatives={campaign.creatives}
          values={values}
          setFieldValue={setFieldValue}
          checkedLocations={visibleCheckedLocations}
          setCheckedLocations={setCheckedLocations}
          subscribedLocations={subscribedLocations}
          setLocationExpanded={setExpanded}
          isSelectAll={isAllLocationsChecked}
          onSelectAll={(e) => {
            handleToggleAllLocations(e.target.checked);
          }}
          setShowCheckbox={setShowCheckbox}
          campaign={campaign}
          onBulkBudgetRecommendationRequested={
            handleBulkBudgetRecommendationRequested
          }
          isBudgetRecommendationEnabled={isBudgetRecommendationEnabled}
        />
      ) : null}

      {fillAdvancedSetting === false && (
        <>
          <div
            className={`location-list-toolbar mb-2 my-3 ${
              showCheckbox ? "with-checkbox" : "without-checkbox"
            }`}
          >
            {showCheckbox && (
              <>
                <Paper className="location-list-checkbox p-0" elevation={2}>
                  <Checkbox
                    color="primary"
                    checked={isAllLocationsChecked}
                    onChange={(e) => {
                      handleToggleAllLocations(e.target.checked);
                    }}
                    className="m-0 p-1"
                  />
                </Paper>
                <Typography
                  variant="body2"
                  component="button"
                  type="button"
                  className="select-all-locations-label"
                  onClick={() =>
                    handleToggleAllLocations(!isAllLocationsChecked)
                  }
                >
                  {isAllLocationsChecked ? "Clear all" : "Select All"}
                </Typography>
              </>
            )}
            <div className="location-list-search">
              <MuiSearchInput value={searchKey} onChange={handleSearchChange} />
            </div>
            <StateCityLocationFilter
              locations={sortedSubscribedLocations}
              value={appliedLocationFilters}
              onApply={setAppliedLocationFilters}
            />
          </div>
          <StateCityAppliedFilterChips
            locations={subscribedLocations}
            value={appliedLocationFilters}
            onChange={setAppliedLocationFilters}
          />
        </>
      )}

      {fillAdvancedSetting === true && (
        <Typography variant="h6" className="mb-3">
          Advanced Settings
        </Typography>
      )}

      <div className="pb-5">
        {fillAdvancedSetting === false && (
          <>
            {filteredSubscribedLocations &&
            filteredSubscribedLocations?.length === 0 ? (
              <div className="my-5 text-center">
                <Alert severity="info">
                  <strong>No location found.</strong>
                </Alert>
              </div>
            ) : (
              <RpVirtualList
                items={filteredSubscribedLocations}
                renderItem={(loc) => (
                  <div
                    className={`location-list-row ${
                      showCheckbox ? "with-checkbox" : "without-checkbox"
                    }`}
                    key={loc.id}
                  >
                    {showCheckbox && (
                      <Paper
                        className="location-list-checkbox location-row-checkbox p-0"
                        elevation={2}
                      >
                        <Checkbox
                          color="primary"
                          checked={checkedLocations.includes(loc.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCheckedLocations((prev) => [...prev, loc.id]);
                            } else {
                              setCheckedLocations((prev) =>
                                prev.filter((id) => id !== loc.id)
                              );
                            }
                          }}
                          className="m-0 p-1"
                        />
                      </Paper>
                    )}
                    <div className="w-100">
                      <Location
                        key={loc.id}
                        loc={loc}
                        locFormData={values[loc.id]}
                        setFieldValue={setFieldValue}
                        setFieldError={setFieldError}
                        expanded={expanded}
                        setExpanded={setExpanded}
                        campaign={campaign}
                        productList={productList}
                        getAdvanceSettingData={getAdvanceSettingData}
                        unCheckedLocations={unCheckedLocations}
                        shouldExpandIt={expanded.includes(loc.id)}
                        onGetBudgetRecommendation={onGetBudgetRecommendation}
                        isBudgetRecommendationEnabled={
                          isBudgetRecommendationEnabled
                        }
                        onBlur={handleInteractionEnd}
                        onFocus={handleInteractionStart}
                      />
                    </div>
                  </div>
                )}
              />
            )}
          </>
        )}
      </div>
      <div className="pb-5">
        {fillAdvancedSetting === true && (
          <RpVirtualList
            items={filledLocations}
            renderItem={(loc) => (
              <LocationAdvancedSetting
                loc={loc}
                locFormData={values[loc.id]}
                key={loc.id}
                setFieldValue={setFieldValue}
                setFieldError={setFieldError}
                expanded={expanded}
                setExpanded={setExpanded}
                campaign={campaign}
                productList={productList}
                getAdvanceSettingData={getAdvanceSettingData}
                handleOnChangeAdvancedSettingError={
                  handleOnChangeAdvancedSettingError
                }
              />
            )}
          />
        )}
      </div>
      <BulkBudgetRecommendationProgress
        open={
          bulkRecommendationProgress.open &&
          (visibleBulkRecommendationLocations.length > 0 ||
            removedBulkRecommendationLocationCount > 0)
        }
        minimized={bulkRecommendationProgress.minimized}
        completed={visibleBulkRecommendationCompleted}
        total={visibleBulkRecommendationLocations.length}
        locations={visibleBulkRecommendationLocations}
        removedLocationCount={removedBulkRecommendationLocationCount}
        onCancel={handleCancelBulkRecommendation}
        onBudgetChange={handleBulkRecommendationBudgetChange}
        onClose={handleCloseBulkRecommendationProgress}
        onRetry={handleRetryBulkRecommendation}
        onToggleMinimized={handleToggleBulkRecommendationMinimized}
      />

      <div className="card total-budget-box mt-4">
        <div className="card-body">
          <div className="total-budget">
            <Typography
              className="mr-2 fw-b mb-2 mb-sm-0"
              component="span"
              variant="body2"
            >
              Your Campaign Total Budget
            </Typography>

            <Button
              component="span"
              variant="outlined"
              disableElevation
              disableRipple
              startIcon={<Icon component={RpSvgIcon} name="money1" />}
            >{`$ ${getTotal}`}</Button>
          </div>
        </div>
      </div>
      {/* {!isEmpty(formError) && <RpSnackbar show message={formError.message} />} */}
      <div className="row justify-content-end align-items-center text-right my-4 actions">
        {subscribedLocations.length && (
          <div className="col-lg-5 mb-4 mb-lg-3">
            <Typography variant="caption">
              {`Selections complete for ${madeSelectionsIn} of ${subscribedLocations.length} locations.`}
            </Typography>
          </div>
        )}
        {campaign_advanced_setting?.is_required &&
          fillAdvancedSetting === false && (
            <>
              {!isPreview && (
                <div className="col-lg-2 mb-3 text-right">
                  <Button
                    color="primary"
                    size="large"
                    variant="outlined"
                    disabled={isSubmitting || isBudgetRecommendationInProgress}
                    aria-disabled={
                      isSubmitting || isBudgetRecommendationInProgress
                    }
                    onClick={onSaveAsDraft}
                    type="submit"
                    fullWidth
                  >
                    Save as Draft
                  </Button>
                </div>
              )}
              <div className="col-lg-3 mb-3 text-right">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting || isBudgetRecommendationInProgress}
                  aria-disabled={
                    isSubmitting || isBudgetRecommendationInProgress
                  }
                  fullWidth
                  disableElevation
                  size="large"
                >
                  {isPreview ? "Confirm Selections" : "Next"}
                </Button>
              </div>
            </>
          )}

        {(!campaign_advanced_setting?.is_required ||
          fillAdvancedSetting === true) && (
          <>
            {campaign_advanced_setting?.is_required && (
              <div className="col-lg-2 mb-3 text-right">
                <Button
                  color="primary"
                  size="large"
                  variant="outlined"
                  disabled={isSubmitting}
                  aria-disabled={isSubmitting}
                  onClick={() => setFillAdvancedSetting(false)}
                  type="button"
                  fullWidth
                >
                  Back
                </Button>
              </div>
            )}
            {!isPreview && (
              <div className="col-lg-2 mb-3 text-right">
                <Button
                  color="primary"
                  size="large"
                  variant="outlined"
                  disabled={isSubmitting || isBudgetRecommendationInProgress}
                  aria-disabled={
                    isSubmitting || isBudgetRecommendationInProgress
                  }
                  onClick={onSaveAsDraft}
                  type="submit"
                  fullWidth
                >
                  Save as Draft
                </Button>
              </div>
            )}
            <div className="col-lg-3 mb-3 text-right">
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={
                  isSubmitting ||
                  !isAdvancedSettingsFormValid ||
                  isBudgetRecommendationInProgress
                }
                aria-disabled={
                  isSubmitting ||
                  !isAdvancedSettingsFormValid ||
                  isBudgetRecommendationInProgress
                }
                fullWidth
                disableElevation
                size="large"
              >
                Confirm Selections
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
